import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  InterviewService,
  InterviewShareService,
  type SharedInterviewDtoPagedResponseDto,
} from '@/services/api';

const interviewSharesSettingsKeys = {
  getInterviewSharesList: (params?: GetInterviewSharesListQueryParams) =>
    ['getInterviewSharesList', params] as const,
};

type GetInterviewSharesListQueryParams = {
  q?: string;
  remainingDayStart?: number;
  remainingDayEnd?: number;
  size?: number;
  page?: number;
};

export const useGetInterviewSharesList = (
  params: GetInterviewSharesListQueryParams,
  options?: Partial<
    UseQueryOptions<SharedInterviewDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<SharedInterviewDtoPagedResponseDto, AxiosError>({
    queryKey: interviewSharesSettingsKeys.getInterviewSharesList(params),
    queryFn: () => InterviewShareService.getApiInterviewShare(params),
    ...options,
  });
};

export const useDeleteSharedInterviewMutation = (
  options?: Partial<
    UseMutationOptions<
      void,
      AxiosError,
      {
        interviewId: string;
        email: string;
        shareId: string;
        candidateId: string;
      }
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { interviewId: string; email: string; shareId: string; candidateId: string }
  >({
    mutationFn: ({ interviewId, email, shareId, candidateId }) =>
      InterviewService.deleteApiInterviewCandidateShareEmailById({
        id: interviewId,
        email,
        shareId,
        candidateId,
      }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: ['getInterviewSharesList'],
        exact: false,
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
